<template>
  <div class="recurring-commissionplan-matrix content">
    <dean-section-title content="true">
      <span slot="title">
        <span class="avenir-heavy">{{ headerTitle }}</span>
      </span>
    </dean-section-title>
    <dean-matrix
      v-if="periodExist"
      :data="matrix"
      :columns="columns"
      :rows="rows"
      @click="openGatePerentageDialog"
    ></dean-matrix>
  </div>
</template>

<script lang="ts">
import { Vue, Component } from 'vue-property-decorator';

import deanMatrix from '@/components/deanComponents/deanMatrix.vue';
import deanTwoColumnLayout from '@/components/deanComponents/deanTwoColumnLayout.vue';
import { plusIcon, editIcon } from '@deanone/dean-components';
import periodsSideMenuMixin from '@/mixins/periodsSideMenuMixin';
import { mixins } from 'vue-class-component';
import { deanDialogBus } from '@/main';
import DeanSectionTitle from '@/components/deanComponents/deanSectionTitle.vue';
import {getDutchDateFormat} from '@/mixins/dateWrapper';

@Component({
  components: {
    DeanSectionTitle,
    deanMatrix,
    deanTwoColumnLayout,
  },
})
export default class Details extends mixins(periodsSideMenuMixin) {
  public matrix: any = [];
  public columns: string[] = [];
  public rows: string[] = [];
  public gates: any[] = [];
  public groups: any[] = [];
  public commissionName: string = '';
  public periodExist: boolean = false;

  get headerTitle() {
    return (
      'commissieplan ' +
      (this.commissionName ? this.commissionName : '')
    );
  }

  get sideMenu(): object {
    return {
      content: {
        title: {
          text:
            'Commissieplan ' + (this.commissionName ? this.commissionName : ''),
        },
        type: 'themed',
        sections: {
          general: {
            items: {
              settings: {
                title: 'Instellingen',
                name: 'standingData.recurringCommissionPlan.edit',
              },
              back: {
                title: 'Terug naar plannenlijst',
                name: 'standingData.recurringCommissionPlans',
              },
            },
          },
          gate: {
            title: 'staffels',
            items: this.gates,
          },
          periods: {
            title: 'periode',
            items: this.periods,
          },
        },
      },
    };
  }

  public created() {
    this.loadMatrix();

    deanDialogBus.$on('percentageChanged', () => {
      this.loadMatrix();
    });
    deanDialogBus.$on('periodSelected', () => {
      this.loadMatrix();
    });
    deanDialogBus.$on('gateChanged', () => {
      this.loadMatrix();
    });
  }

  public loadMatrix() {
    this.$store.dispatch('setLoadOverlay', true);
    this.$store
      .dispatch('requestRecurringCommissionPlanPeriods', {
        id: this.$route.params.planId,
      })
      .then(
        (periodResponse: any) => {

          this.commissionName = periodResponse.data.data.name;

          const transformPeriodMapper: any = [];

          periodResponse.data.data.periods.forEach((period: any) => {
              const periodData = {
                selected: this.$route.params.startDate === period.startDate &&
                this.$route.params.endDate === period.endDate ? true : false,
                name: period.startDate + ' - ' + period.endDate,
                label: getDutchDateFormat(period.startDate) + ' - ' + getDutchDateFormat(period.endDate),
                startDate: period.startDate,
                endDate: period.endDate,
              };
              transformPeriodMapper.push(periodData);
          });
          this.transformPeriods(
            transformPeriodMapper,
            'standingData.recurringCommissionPlans.matrix.filter',
          );

          return this.$store.dispatch('requestRecurringCommissionPlanGates', {
            planId: this.$route.params.planId,
            sort: 'name',
          });
        },
        (error: any) => {
          this.$toast(this.$store.state.main.notification.messages.couldNotReceiveData);
        },
      )
      .then(
        (rcpGatesResponse: any) => {
          interface GateParams {
            id: number;
            planId: number;
            startDate: string;
            endDate: string;
            modal: boolean;
          }

          interface Gate {
            title: string;
            name: string;
            params: GateParams;
          }

          this.gates = rcpGatesResponse.data.data.map((gate: any) => {
            const newGate: Gate = {
              title: gate.name,
              name: 'StandingDataGateDialog',
              params: {
                id: gate.id,
                planId: gate.planId,
                startDate: this.$route.params.startDate,
                endDate: this.$route.params.endDate,
                modal: true,
              },
            };

            return newGate;
          });

          this.rows = rcpGatesResponse.data.data.map((gate: any) => gate.name);

          this.gates.push({
            title: 'Staffel toevoegen',
            name: 'StandingDataGateDialog',
            icon: plusIcon,
            params: {
              modal: true,
              planId: this.$route.params.planId,
            },
          });

          return this.$store.dispatch('requestGroups', {type: 'recurringCommissionGroup', sort: 'name'});
        },
        (error: any) => {
          this.$toast(this.$store.state.main.notification.messages.couldNotReceiveData);
        },
      )
      .then(
        (groupResponse: any) => {
          this.columns = groupResponse.data.data.map((group: any) => group.name);

          this.groups = groupResponse.data.data.map((group: any) => {
            return {
              id: group.id,
              name: group.name,
            };
          });

          const percentageListRequestMapper = {
            planId: this.$route.params.planId,
            startDate: this.$route.params.startDate,
            endDate: this.$route.params.endDate,
          };
          return this.$store.dispatch('requestRecurringCommissionPlanGatePercentages', percentageListRequestMapper);
        },
        (error: any) => {
          this.$toast(this.$store.state.main.notification.messages.couldNotReceiveData);
        },
      )
      .then(
        (percentageResponse: any) => {
          const gatesMatrix: any = {};
          let groupsMatrix: any = {};

          for (const gateName of this.rows) {
            gatesMatrix[gateName] = true;

            for (const groupName of this.columns) {
              groupsMatrix[groupName] = {
                label: '',
                icon: 'plus',
                params: {
                  gateId: (this.gates.find((gate: any) => gate.title === gateName)).params.id,
                  groupId: (this.groups.find((group: any) => group.name === groupName)).id,
                  startDate: this.$route.params.startDate,
                  endDate: this.$route.params.endDate,
                },
              };
            }

            gatesMatrix[gateName] = groupsMatrix;
            groupsMatrix = {};
          }

          // populate matrix with existing percentages
          percentageResponse.data.data.forEach((percentage: any) => {
            gatesMatrix[percentage.gateName][percentage.groupName] = {
              label: percentage.percentage.toFixed(2) + '%',
              icon: 'edit',
              params: {
                id: percentage.id,
                gateId: percentage.gateId,
                groupId: percentage.groupId,
                planId: this.$route.params.id,
                startDate: percentage.startDate,
                endDate: percentage.endDate,
                percentage: percentage.percentage.toFixed(2),
              },
            };
          });

          const matrix: any = [];

          // convert string keys (gateName and groupName) to indexes in order to deanMatrix be able to process it
          for (const [key, value] of Object.entries(gatesMatrix)) {
            let matrixGroup: any = [];
            matrixGroup = value;
            const groups = [];
            for (const [group, pct] of Object.entries(matrixGroup)) {
               groups[this.columns.indexOf(group)] = pct;
            }
            matrix[this.rows.indexOf(key)] = groups;
          }
          this.matrix = matrix;
        },
        (error: any) => {
          this.$toast(this.$store.state.main.notification.messages.couldNotReceiveData);
        },
      )
      .finally(() => {
        if (this.periods.length && this.matrix.length) {
          this.periodExist = true;
        }
        this.$store.dispatch('updateSideMenu', this.sideMenu);
        this.$store.dispatch('setLoadOverlay', false);
      });
  }

  public openGatePerentageDialog(value: any) {
    const data = this.matrix[value[0]][value[1]].params;
    deanDialogBus.$emit('open', 'StandingDataCommissionPercentageDialog', data);
  }

  // public mounted() {
  //   deanDialogBus.$on('percentageChanged', () => {
  //     this.loadMatrix();
  //   });
  //   deanDialogBus.$on('periodSelected', () => {
  //     this.loadMatrix();
  //   });
  //   deanDialogBus.$on('gateChanged', () => {
  //     this.loadMatrix();
  //   });
  // }

  public destroyed() {
    deanDialogBus.$off(['gateChanged', 'percentageChanged', 'periodSelected']);
  }
}
</script>

<style lang="scss" scoped></style>
